// The date register, the service history, and the exceptions log they file into.
//
// The database owns every rule (20260829130000_key_dates.sql): it derives a
// probation end when a join date is recorded, files an hr_exception when somebody
// moves a derived date, reminds once, and lapses what nobody actioned. This module
// names those reads and writes and shapes their rows.
import { supabase } from './supabase';
import { write } from './write';
import { addDaysToKey, orgDateKey } from './dates';

export type DateType =
  | 'probation_end'
  | 'internship_end'
  | 'contract_end'
  | 'notice_expiry'
  | 'handbook_gap'
  | 'document_expiry';

export type KeyDateStatus = 'pending' | 'actioned' | 'lapsed';

export type KeyDate = {
  id: string;
  profile_id: string;
  date_type: DateType;
  date: string;
  source: 'derived' | 'manual';
  reminder_days: number;
  status: KeyDateStatus;
  actioned_by: string | null;
  document_link: string | null;
};

export type DueDate = KeyDate & { full_name: string | null };

export type EngagementEvent = {
  id: string;
  profile_id: string;
  from_type: string;
  to_type: string;
  effective_date: string;
  document_link: string | null;
  continuous: boolean;
  gap_reason: string | null;
  recorded_at: string;
};

export type HrException = {
  id: string;
  subject_type: string;
  subject_id: string;
  profile_id: string | null;
  action: string;
  standard_value: string | null;
  actual_value: string | null;
  reason: string;
  approved_by: string | null;
  created_at: string;
};

/** The register's own vocabulary, in the order HR thinks about it. */
export const DATE_TYPES: { id: DateType; label: string }[] = [
  { id: 'probation_end', label: 'Probation ends' },
  { id: 'internship_end', label: 'Internship ends' },
  { id: 'contract_end', label: 'Contract ends' },
  { id: 'notice_expiry', label: 'Notice expires' },
  { id: 'document_expiry', label: 'Document expires' },
  // handbook_gap is generated by R9's sweep, never chosen by hand.
];

export const dateTypeLabel = (t: string) =>
  DATE_TYPES.find((d) => d.id === t)?.label ?? t.replace(/_/g, ' ');

/** A read's rows, or why there are none. A failed read is not an empty list
 *  (UI-CONVENTIONS §7): the caller renders LoadError on `error`, never "nothing yet". */
export type Read<T> = { data: T; error: { message: string } | null };

const KD =
  'id, profile_id, date_type, date, source, reminder_days, status, actioned_by, document_link';

export async function listKeyDates(profileId: string): Promise<Read<KeyDate[]>> {
  if (!supabase) return { data: [], error: null };
  const { data, error } = await supabase
    .from('key_date')
    .select(KD)
    .eq('profile_id', profileId)
    .order('date');
  return { data: (data as KeyDate[]) ?? [], error };
}

/** Everything overdue or coming up, for the HR queue. */
export async function listDueDates(): Promise<Read<DueDate[]>> {
  if (!supabase) return { data: [], error: null };
  const { data, error } = await supabase
    .from('key_date')
    // Named FK: key_date references profiles three times (profile_id,
    // actioned_by, created_by), so a bare embed is ambiguous.
    .select(`${KD}, profiles!key_date_profile_id_fkey(full_name)`)
    .in('status', ['pending', 'lapsed'])
    .order('date')
    .limit(50);
  const rows = ((data as Record<string, unknown>[]) ?? []).map((r) => {
    const p = Array.isArray(r.profiles) ? r.profiles[0] : r.profiles;
    return {
      ...(r as unknown as KeyDate),
      full_name: (p as { full_name?: string | null } | null)?.full_name ?? null,
    };
  });
  return { data: rows, error };
}

type Result = { error: { message: string } | null };

export async function addKeyDate(input: {
  profileId: string;
  dateType: DateType;
  date: string;
  documentLink?: string;
}): Promise<Result> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.from('key_date').insert({
    profile_id: input.profileId,
    date_type: input.dateType,
    date: input.date,
    document_link: input.documentLink?.trim() || null,
  });
  return { error };
}

/** `actioned_by` is not sent — the trigger stamps it from the JWT. A zero-row
 *  update is a refusal (lib/write): RLS filtered the row out, nothing was marked. */
export async function actionKeyDate(id: string): Promise<Result> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const r = await write(supabase.from('key_date').update({ status: 'actioned' }).eq('id', id));
  if (r.ok) return { error: null };
  return {
    error: r.reason === 'refused' ? { message: 'You can’t mark this date done.' } : r.error,
  };
}

export async function listEvents(profileId: string): Promise<Read<EngagementEvent[]>> {
  if (!supabase) return { data: [], error: null };
  const { data, error } = await supabase
    .from('engagement_event')
    .select(
      'id, profile_id, from_type, to_type, effective_date, document_link, continuous, gap_reason, recorded_at',
    )
    .eq('profile_id', profileId)
    .order('effective_date', { ascending: false });
  return { data: (data as EngagementEvent[]) ?? [], error };
}

export async function addEvent(input: {
  profileId: string;
  fromType: string;
  toType: string;
  effectiveDate: string;
  continuous: boolean;
  gapReason?: string;
  documentLink?: string;
}): Promise<Result> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.from('engagement_event').insert({
    profile_id: input.profileId,
    from_type: input.fromType.trim(),
    to_type: input.toType.trim(),
    effective_date: input.effectiveDate,
    continuous: input.continuous,
    gap_reason: input.continuous ? null : input.gapReason?.trim() || null,
    document_link: input.documentLink?.trim() || null,
  });
  return { error };
}

/**
 * R7.4 — the central list. Also filtered per record on the person's own page.
 *
 * The org-wide list is read a month at a time (bug report 2026-10-06: "an
 * ever growing list"). It used to stop silently at 50 rows, so older ones
 * could not be reached at all. created_at is an instant, so a day is fetched
 * either side and the month is cut on the org's calendar (UI-CONVENTIONS §2).
 */
export async function listExceptions(
  profileId?: string,
  month?: { from: string; to: string; tz: string },
): Promise<Read<HrException[]>> {
  if (!supabase) return { data: [], error: null };
  let q = supabase
    .from('hr_exception')
    .select(
      'id, subject_type, subject_id, profile_id, action, standard_value, actual_value, reason, approved_by, created_at',
    )
    .order('created_at', { ascending: false });
  if (profileId) q = q.eq('profile_id', profileId).limit(50);
  if (month)
    q = q
      .gte('created_at', `${addDaysToKey(month.from, -1)}T00:00:00Z`)
      .lt('created_at', `${addDaysToKey(month.to, 2)}T00:00:00Z`);
  const { data, error } = await q;
  const rows = (data as HrException[]) ?? [];
  return {
    data: month
      ? rows.filter((r) => {
          const day = orgDateKey(r.created_at, month.tz);
          return day >= month.from && day <= month.to;
        })
      : rows,
    error,
  };
}
